import type { NetworkData } from '@dynamic-labs-sdk/client';
import { getActiveNetworkData } from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  getDefaultClient,
  getNetworkDataForNetworkId,
} from '@dynamic-labs-sdk/client/core';
import type { EvmWalletAccount } from '@dynamic-labs-sdk/evm';
import { mapNetworkDataToViemChain } from '@dynamic-labs-sdk/evm/viem';
import type { ZerodevBundlerProvider } from '@dynamic-labs/sdk-api-core';
import {
  createKernelAccountClient,
  getUserOperationGasPrice,
} from '@zerodev/sdk';
import type { Hex } from 'viem';
import { createPublicClient, http } from 'viem';
import type { SignAuthorizationReturnType } from 'viem/accounts';

import type { KernelClient } from '../KernelClient.types';
import { createKernelAccount } from '../utils/createKernelAccount';
import { getPaymasterConfig } from '../utils/getPaymasterConfig';
import { getZerodevRpc } from '../utils/getZerodevRpc';

export type Eip7702Authorization = SignAuthorizationReturnType;

type CreateKernelClientForWalletAccountParams = {
  bundlerProvider?: ZerodevBundlerProvider;
  bundlerRpc?: string;
  eip7702Auth?: SignAuthorizationReturnType;
  gasTokenAddress?: Hex;
  networkId?: string;
  paymasterRpc?: string;
  smartWalletAccount: EvmWalletAccount;
  withSponsorship?: boolean;
};

/**
 * Creates a KernelClient instance for a given smart wallet account.
 *
 * @param params.smartWalletAccount - The smart wallet account to create the KernelClient for.
 * @param [params.bundlerProvider] - A custom bundler provider to use
 * @param [params.bundlerRpc] - A custom bundler RPC to use
 * @param [params.networkId] - The network ID to use for the KernelClient.
 * If not provided, the network ID will be fetched from the active network data.
 * @param [params.paymasterRpc] - A custom paymaster RPC to use
 * @param [params.gasTokenAddress] - The address of a custom ERC20 token to use as a gas token
 * @param [params.withSponsorship] - Whether to use sponsorship for the KernelClient or not (default is true).
 * @param [params.eip7702Auth] - A pre-signed EIP-7702 authorization. When provided, the kernel client uses 
 * this authorization instead of signing a new one internally. Useful for singleUse MFA flows where you need
 * to separate the authorization signing step from the transaction step.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to a KernelClient instance.
 */
export const createKernelClientForWalletAccount = async (
  {
    smartWalletAccount,
    withSponsorship = true,
    bundlerProvider,
    networkId,
    bundlerRpc: bundlerRpcOverride,
    paymasterRpc: paymasterRpcOverride,
    gasTokenAddress,
    eip7702Auth,
  }: CreateKernelClientForWalletAccountParams,
  client = getDefaultClient()
): Promise<KernelClient> => {
  let networkData: NetworkData | undefined;

  if (networkId) {
    networkData = getNetworkDataForNetworkId(
      { chain: 'EVM', networkId },
      client
    );

    assertDefined(
      networkData,
      `No network data found for specified network ID: ${networkId}. Make sure the network is enabled for your project.`
    );
  } else {
    const activeNetworkData = await getActiveNetworkData(
      { walletAccount: smartWalletAccount },
      client
    );

    networkData = activeNetworkData.networkData;

    assertDefined(
      networkData,
      `No active network data found for this wallet account.`
    );
  }

  const viemChain = mapNetworkDataToViemChain(networkData);

  const bundlerRpc =
    bundlerRpcOverride ??
    getZerodevRpc(
      {
        bundlerProvider,
        networkId: networkData.networkId,
        rpcType: 'bundler',
      },
      client
    );

  const bundlerTransport = http(bundlerRpc);

  const publicClient = createPublicClient({
    chain: viemChain,
    transport: bundlerTransport,
  });

  const account = await createKernelAccount(
    {
      eip7702Auth,
      publicClient,
      smartWalletAccount,
    },
    client
  );

  const paymasterRpc =
    paymasterRpcOverride ??
    getZerodevRpc(
      {
        bundlerProvider,
        networkId: networkData.networkId,
        rpcType: 'paymaster',
      },
      client
    );

  const paymasterConfig = withSponsorship
    ? getPaymasterConfig({
        chain: viemChain,
        gasTokenAddress,
        paymasterRpc,
      })
    : {};

  return createKernelAccountClient({
    account,
    bundlerTransport,
    chain: viemChain,
    client: publicClient,
    userOperation: {
      estimateFeesPerGas: async ({ bundlerClient }) =>
        getUserOperationGasPrice(bundlerClient),
    },
    ...paymasterConfig,
  });
};
